import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useAuth } from '../auth/AuthContext'
import { listOutbox, type OutboxRecord } from './db'
import { applyDeviceCommands, buildDeviceReport, sendDeviceReport } from './diagnostics'
import { summarizeQueue } from './outbox'
import { isSyncSessionActive, syncOutbox, type SyncProgress } from './sync'

interface SyncContextValue {
  browserOnline: boolean
  syncing: boolean
  queue: OutboxRecord[]
  pendingCount: number
  attentionCount: number
  /** Действия, отклонённые сервером насовсем: не отправляются и не блокируют остальные. */
  obsoleteCount: number
  lastCompletedAt: number | null
  lastResult: SyncProgress | null
  /** Когда отчёт о состоянии устройства последний раз принят сервером. */
  lastReportAt: number | null
  refreshQueue(): Promise<void>
  syncNow(): Promise<SyncProgress | null>
  /** Отправляет отчёт о состоянии устройства и выполняет команды администратора. true — сервер принял отчёт. */
  sendReport(force?: boolean): Promise<boolean>
}

const SyncContext = createContext<SyncContextValue | null>(null)

/** Отчёт при неизменной очереди — не чаще раза в 15 минут; при изменениях — не чаще раза в 20 секунд. */
const REPORT_UNCHANGED_INTERVAL = 15 * 60_000
const REPORT_CHANGED_INTERVAL = 20_000
const REPORT_TIMER_INTERVAL = 5 * 60_000

export function SyncProvider({ children }: { children: ReactNode }) {
  const { user, syncSession } = useAuth()
  const currentUserId = useRef(user?.id ?? null)
  useLayoutEffect(() => { currentUserId.current = user?.id ?? null }, [user?.id])
  const [browserOnline, setBrowserOnline] = useState(navigator.onLine)
  const [queue, setQueue] = useState<OutboxRecord[]>([])
  const [syncing, setSyncing] = useState(false)
  const [lastCompletedAt, setLastCompletedAt] = useState<number | null>(null)
  const [lastResult, setLastResult] = useState<SyncProgress | null>(null)
  const [lastReportAt, setLastReportAt] = useState<number | null>(null)
  const lastCompletedRef = useRef<number | null>(null)
  const lastResultRef = useRef<SyncProgress | null>(null)
  const reportState = useRef<{ at: number; signature: string; inFlight: boolean }>({ at: 0, signature: '', inFlight: false })

  const refreshQueue = useCallback(async () => {
    const ownerId = user?.id
    if (!ownerId) {
      setQueue([])
      return
    }
    const records = await listOutbox(ownerId)
    if (currentUserId.current === ownerId) setQueue(records)
  }, [user?.id])

  const sendReport = useCallback(async (force = false, executedCommands: string[] = []): Promise<boolean> => {
    const session = syncSession
    if (!session || !navigator.onLine || !isSyncSessionActive(session) || reportState.current.inFlight) return false
    const records = await listOutbox(session.userId)
    const signature = JSON.stringify(summarizeQueue(records))
    const now = Date.now()
    const sinceLast = now - reportState.current.at
    if (!force && executedCommands.length === 0) {
      if (signature === reportState.current.signature && sinceLast < REPORT_UNCHANGED_INTERVAL) return false
      if (sinceLast < REPORT_CHANGED_INTERVAL) return false
    }
    reportState.current.inFlight = true
    try {
      let acknowledged = executedCommands
      let reload = false
      // Первый круг — отчёт; второй — подтверждение выполненных по нему команд (сервер убирает их из ожидающих).
      for (let round = 0; round < 2; round += 1) {
        const report = await buildDeviceReport(session.userId, { records: round === 0 ? records : undefined, lastSyncAt: lastCompletedRef.current, lastResult: lastResultRef.current, executedCommands: acknowledged })
        const commands = await sendDeviceReport(session, report)
        if (!isSyncSessionActive(session)) return false
        reportState.current = { at: Date.now(), signature, inFlight: true }
        setLastReportAt(reportState.current.at)
        const fresh = commands.filter((command) => !acknowledged.includes(command.id))
        if (fresh.length === 0) break
        const outcome = await applyDeviceCommands(session.userId, fresh)
        await refreshQueue()
        reload = reload || outcome.reload
        if (outcome.executed.length === 0) break
        acknowledged = outcome.executed
      }
      if (reload) window.setTimeout(() => window.location.reload(), 800)
      return true
    } catch {
      return false
    } finally {
      reportState.current.inFlight = false
    }
  }, [refreshQueue, syncSession])

  const syncNow = useCallback(async () => {
    const session = syncSession
    if (!session || !navigator.onLine || !isSyncSessionActive(session)) return null
    setSyncing(true)
    try {
      const result = await syncOutbox(session)
      if (isSyncSessionActive(session)) {
        lastResultRef.current = result
        lastCompletedRef.current = Date.now()
        setLastResult(result)
        setLastCompletedAt(lastCompletedRef.current)
      }
      return result
    } finally {
      if (isSyncSessionActive(session)) {
        setSyncing(false)
        await refreshQueue()
        void sendReport()
      }
    }
  }, [refreshQueue, sendReport, syncSession])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQueue([])
      setSyncing(false)
      setLastCompletedAt(null)
      setLastResult(null)
      setLastReportAt(null)
      lastCompletedRef.current = null
      lastResultRef.current = null
      reportState.current = { at: 0, signature: '', inFlight: false }
      void refreshQueue()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [refreshQueue, syncSession])

  useEffect(() => {
    const queueChanged = () => void refreshQueue()
    const syncRequested = () => {
      if (navigator.onLine) void syncNow()
    }
    const online = () => {
      setBrowserOnline(true)
      void syncNow()
    }
    const offline = () => setBrowserOnline(false)
    const focus = () => {
      if (navigator.onLine) void syncNow()
    }
    const visibility = () => {
      if (document.visibilityState === 'visible' && navigator.onLine) void syncNow()
    }
    const workerMessage = (event: MessageEvent) => {
      if (event.data?.type === 'OUTBOX_UPDATED') void refreshQueue()
      if (event.data?.type === 'SYNC_REQUESTED') void syncNow()
    }

    window.addEventListener('amalthea:queue-changed', queueChanged)
    window.addEventListener('amalthea:sync-requested', syncRequested)
    window.addEventListener('online', online)
    window.addEventListener('offline', offline)
    window.addEventListener('focus', focus)
    document.addEventListener('visibilitychange', visibility)
    navigator.serviceWorker?.addEventListener('message', workerMessage)
    return () => {
      window.removeEventListener('amalthea:queue-changed', queueChanged)
      window.removeEventListener('amalthea:sync-requested', syncRequested)
      window.removeEventListener('online', online)
      window.removeEventListener('offline', offline)
      window.removeEventListener('focus', focus)
      document.removeEventListener('visibilitychange', visibility)
      navigator.serviceWorker?.removeEventListener('message', workerMessage)
    }
  }, [refreshQueue, syncNow])

  useEffect(() => {
    if (!syncSession || !navigator.onLine) return
    const timer = window.setTimeout(() => void syncNow(), 0)
    return () => window.clearTimeout(timer)
  }, [syncNow, syncSession])

  // Регулярный отчёт о состоянии, пока приложение открыто: администратор видит очередь устройства без доступа к телефону.
  useEffect(() => {
    if (!syncSession) return
    const timer = window.setInterval(() => { if (navigator.onLine && document.visibilityState === 'visible') void sendReport() }, REPORT_TIMER_INTERVAL)
    return () => window.clearInterval(timer)
  }, [sendReport, syncSession])

  useEffect(() => {
    if (!browserOnline || syncing) return
    const nextRetry = queue
      .filter((item) => item.userId === syncSession?.userId)
      .filter((item) => item.state === 'retry')
      .reduce<number | null>((nearest, item) => nearest === null ? item.nextAttemptAt : Math.min(nearest, item.nextAttemptAt), null)
    if (nextRetry === null) return
    const timer = window.setTimeout(() => void syncNow(), Math.max(50, nextRetry - Date.now()))
    return () => window.clearTimeout(timer)
  }, [browserOnline, queue, syncNow, syncing, syncSession?.userId])

  const visibleQueue = useMemo(
    () => user ? queue.filter((item) => item.userId === user.id) : [],
    [queue, user],
  )

  const value = useMemo(() => {
    const summary = summarizeQueue(visibleQueue)
    return {
      browserOnline,
      syncing,
      queue: visibleQueue,
      pendingCount: summary.pending,
      attentionCount: summary.attention,
      obsoleteCount: summary.obsolete,
      lastCompletedAt,
      lastResult,
      lastReportAt,
      refreshQueue,
      syncNow,
      sendReport: (force?: boolean) => sendReport(force ?? false),
    }
  }, [browserOnline, syncing, visibleQueue, lastCompletedAt, lastResult, lastReportAt, refreshQueue, syncNow, sendReport])

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>
}

export function useSync(): SyncContextValue {
  const value = useContext(SyncContext)
  if (!value) throw new Error('SyncProvider is missing')
  return value
}
